/**
 * Media Utilities
 * 
 * Centralized utilities for camera/microphone access with proper
 * browser compatibility checks and error handling.
 * 
 * CRITICAL: navigator.mediaDevices is undefined in:
 * - Non-HTTPS contexts (except localhost)
 * - Older browsers without WebRTC support
 * - iOS Safari in certain WebView contexts
 * - When permissions are blocked at OS level
 */

export interface MediaDevicesError {
  type: 'not_supported' | 'not_secure' | 'permission_denied' | 'not_found' | 'in_use' | 'unknown';
  message: string;
  originalError?: Error;
}

/**
 * Check if MediaDevices API is available
 */
export function isMediaDevicesSupported(): boolean {
  return !!(navigator.mediaDevices && typeof navigator.mediaDevices.getUserMedia === 'function');
}

/**
 * Check if we're in a secure context (HTTPS or localhost)
 */
export function isSecureContext(): boolean {
  // window.isSecureContext is the standard way to check
  if (typeof window.isSecureContext === 'boolean') {
    return window.isSecureContext;
  }
  // Fallback for older browsers
  return window.location.protocol === 'https:' || 
         window.location.hostname === 'localhost' ||
         window.location.hostname === '127.0.0.1';
}

/**
 * Get a user-friendly error message for media access failures
 */
export function getMediaErrorDetails(error: unknown): MediaDevicesError {
  // Check for API availability first
  if (!isMediaDevicesSupported()) {
    if (!isSecureContext()) {
      return {
        type: 'not_secure',
        message: 'Camera/microphone access requires HTTPS. Please use a secure connection.',
      };
    }
    return {
      type: 'not_supported',
      message: 'Your browser does not support camera/microphone access. Please try Chrome, Safari, or Firefox.',
    };
  }

  // Handle standard DOMException errors
  if (error instanceof DOMException || (error instanceof Error && 'name' in error)) {
    const err = error as DOMException;
    switch (err.name) {
      case 'NotAllowedError':
      case 'PermissionDeniedError':
        return {
          type: 'permission_denied',
          message: 'Camera/microphone permission was denied. Please allow access in your browser settings.',
          originalError: err,
        };
      case 'NotFoundError':
      case 'DevicesNotFoundError':
        return {
          type: 'not_found',
          message: 'No camera or microphone found. Please connect a device and try again.',
          originalError: err,
        };
      case 'NotReadableError':
      case 'TrackStartError':
        return {
          type: 'in_use',
          message: 'Camera/microphone is already in use by another application.',
          originalError: err,
        };
      case 'OverconstrainedError':
        return {
          type: 'not_found',
          message: 'The requested camera settings are not supported by your device.',
          originalError: err,
        };
      case 'AbortError':
        return {
          type: 'unknown',
          message: 'Camera/microphone access was interrupted. Please try again.',
          originalError: err,
        };
      case 'SecurityError':
        return {
          type: 'not_secure',
          message: 'Camera/microphone access is blocked due to security restrictions.',
          originalError: err,
        };
    }
  }

  // Generic error
  const message = error instanceof Error ? error.message : String(error);
  return {
    type: 'unknown',
    message: `Failed to access camera/microphone: ${message}`,
    originalError: error instanceof Error ? error : undefined,
  };
}

/**
 * Request camera access with proper error handling
 * 
 * @throws MediaDevicesError with user-friendly message
 */
export async function requestCamera(
  constraints: MediaStreamConstraints['video'] = { facingMode: 'environment' }
): Promise<MediaStream> {
  // Pre-flight check
  if (!isMediaDevicesSupported()) {
    const error = getMediaErrorDetails(new Error('MediaDevices not supported'));
    throw new Error(error.message);
  }

  try {
    return await navigator.mediaDevices.getUserMedia({
      video: constraints,
    });
  } catch (err) {
    const error = getMediaErrorDetails(err);
    console.error('[Media] Camera access failed:', error.type, error.message);
    throw new Error(error.message);
  }
}

/**
 * Request microphone access with proper error handling
 * 
 * @throws MediaDevicesError with user-friendly message
 */
export async function requestMicrophone(
  constraints: MediaStreamConstraints['audio'] = true
): Promise<MediaStream> {
  // Pre-flight check
  if (!isMediaDevicesSupported()) {
    const error = getMediaErrorDetails(new Error('MediaDevices not supported'));
    throw new Error(error.message);
  }

  try {
    return await navigator.mediaDevices.getUserMedia({
      audio: constraints,
    });
  } catch (err) {
    const error = getMediaErrorDetails(err);
    console.error('[Media] Microphone access failed:', error.type, error.message);
    throw new Error(error.message);
  }
}

/**
 * Request both camera and microphone
 */
export async function requestCameraAndMicrophone(
  videoConstraints: MediaStreamConstraints['video'] = { facingMode: 'environment' },
  audioConstraints: MediaStreamConstraints['audio'] = true
): Promise<MediaStream> {
  if (!isMediaDevicesSupported()) {
    const error = getMediaErrorDetails(new Error('MediaDevices not supported'));
    throw new Error(error.message);
  }

  try {
    return await navigator.mediaDevices.getUserMedia({
      video: videoConstraints,
      audio: audioConstraints,
    });
  } catch (err) {
    const error = getMediaErrorDetails(err);
    console.error('[Media] Camera/microphone access failed:', error.type, error.message);
    throw new Error(error.message);
  }
}

/**
 * Enumerate available media devices
 */
export async function getAvailableDevices(): Promise<MediaDeviceInfo[]> {
  if (!isMediaDevicesSupported()) {
    return [];
  }

  try {
    return await navigator.mediaDevices.enumerateDevices();
  } catch (err) {
    console.error('[Media] Failed to enumerate devices:', err);
    return [];
  }
}

/**
 * Check if camera is available (has at least one video input)
 */
export async function hasCameraAvailable(): Promise<boolean> {
  const devices = await getAvailableDevices();
  return devices.some(d => d.kind === 'videoinput');
}

/**
 * Check if microphone is available (has at least one audio input)
 */
export async function hasMicrophoneAvailable(): Promise<boolean> {
  const devices = await getAvailableDevices();
  return devices.some(d => d.kind === 'audioinput');
}

/**
 * Stop all tracks in a MediaStream
 */
export function stopMediaStream(stream: MediaStream | null): void {
  if (stream) {
    stream.getTracks().forEach(track => {
      track.stop();
    });
  }
}
